import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { AlignLeft, Hash, Clock3, ArrowRight } from 'lucide-react';

const ScheduleVideosModal = ({ open, onOpenChange, videos = [], oplae, onSave }) => {
  // Guard against null oplae or empty videos
  if (!oplae || videos.length === 0) return null;

  const [videoSettings, setVideoSettings] = useState(
    videos.reduce((acc, video) => ({
      ...acc,
      [video.id]: {
        caption: '',
        hashtags: '',
        scheduledFor: null
      }
    }), {})
  );
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const currentVideo = videos[currentVideoIndex];

  // Guard against null currentVideo
  if (!currentVideo) return null;

  const handleNext = () => {
    if (currentVideoIndex < videos.length - 1) {
      setCurrentVideoIndex(prev => prev + 1);
    } else {
      onSave(videoSettings);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] bg-gray-900 border-gray-800 text-gray-100">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold flex items-center gap-3">
            <div 
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: oplae?.color || '#7C3AED' }}
            />
            Configure Videos for {oplae?.title || 'Oplae'}
          </DialogTitle>
        </DialogHeader>

        <div className="py-6">
          {/* Progress indicator */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400">
                Video {currentVideoIndex + 1} of {videos.length}
              </span>
              <Badge variant="secondary" className="bg-gray-800 text-gray-300">
                {Math.round(((currentVideoIndex + 1) / videos.length) * 100)}%
              </Badge>
            </div>
            <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
              <div 
                className="h-full rounded-full transition-all duration-300"
                style={{ 
                  width: `${((currentVideoIndex + 1) / videos.length) * 100}%`,
                  backgroundColor: oplae?.color || '#7C3AED'
                }}
              />
            </div>
          </div>

          {/* Video preview */}
          <div className="aspect-[9/16] rounded-lg overflow-hidden bg-gray-800 mb-6">
            <img
              src={currentVideo.thumbnail || currentVideo.preview_url}
              alt={`Video ${currentVideoIndex + 1}`}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Settings form */}
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-300 mb-1.5 flex items-center gap-2">
                <AlignLeft className="w-4 h-4" />
                Caption
              </label>
              <textarea
                value={videoSettings[currentVideo.id]?.caption || ''}
                onChange={(e) => setVideoSettings(prev => ({
                  ...prev,
                  [currentVideo.id]: {
                    ...prev[currentVideo.id],
                    caption: e.target.value
                  }
                }))}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-100 
                          focus:ring-2 focus:ring-purple-500/30 focus:border-purple-400 min-h-[100px]"
                placeholder="Enter caption for this video..."
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-300 mb-1.5 flex items-center gap-2">
                <Hash className="w-4 h-4" />
                Hashtags
              </label>
              <input
                type="text"
                value={videoSettings[currentVideo.id]?.hashtags || ''}
                onChange={(e) => setVideoSettings(prev => ({
                  ...prev,
                  [currentVideo.id]: {
                    ...prev[currentVideo.id],
                    hashtags: e.target.value
                  }
                }))}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-100 
                          focus:ring-2 focus:ring-purple-500/30 focus:border-purple-400"
                placeholder="#viral #trending"
              />
            </div>

            {oplae?.scheduling === 'manual' && (
              <div>
                <label className="text-sm font-medium text-gray-300 mb-1.5 flex items-center gap-2">
                  <Clock3 className="w-4 h-4" />
                  Schedule For
                </label>
                <input
                  type="datetime-local"
                  value={videoSettings[currentVideo.id]?.scheduledFor || ''}
                  onChange={(e) => setVideoSettings(prev => ({
                    ...prev,
                    [currentVideo.id]: {
                      ...prev[currentVideo.id],
                      scheduledFor: e.target.value
                    }
                  }))}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-100 
                            focus:ring-2 focus:ring-purple-500/30 focus:border-purple-400"
                />
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="text-gray-400 hover:text-gray-300"
          >
            Cancel
          </Button>
          <Button
            onClick={handleNext}
            className="bg-purple-500 hover:bg-purple-600 text-white"
          >
            {currentVideoIndex < videos.length - 1 ? (
              <>
                Next Video
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            ) : (
              'Complete Setup'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ScheduleVideosModal; 